import { ExternalLink, FileText } from "lucide-react";
import { formatDate } from "~/lib/format";

const REGISTRY_BASE_URL = "https://verejnerejstriky.msp.gov.cz";

function buildRegistryUrl(ico: string): string {
	return `${REGISTRY_BASE_URL}/ico/${ico}?type=SL`;
}

// --- Types ---

type DocumentType =
	| "ANNUAL_REPORT"
	| "FINANCIAL_STATEMENT"
	| "BALANCE_SHEET"
	| "PROFIT_LOSS_STATEMENT"
	| "ARTICLES_OF_ASSOCIATION"
	| "AUDITOR_REPORT"
	| "OTHER";

type ProcessingStatus =
	| "PENDING"
	| "QUEUED"
	| "PROCESSING"
	| "COMPLETED"
	| "FAILED"
	| "SKIPPED"
	| "ORPHANED";

export interface CompanyDocument {
	id: string;
	title: string;
	documentType: DocumentType;
	year: number | null;
	filingDate: string | null;
	sourceUrl: string | null;
	fileSize: number | null;
	pageCount: number | null;
	mimeType: string | null;
	ocrStatus: ProcessingStatus;
	extractionStatus: ProcessingStatus;
	createdAt: string;
}

export interface DocumentsResponse {
	documents: CompanyDocument[];
	total: number;
}

// --- Helpers ---

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
	ANNUAL_REPORT: "Výroční zpráva",
	FINANCIAL_STATEMENT: "Účetní závěrka",
	BALANCE_SHEET: "Rozvaha",
	PROFIT_LOSS_STATEMENT: "Výkaz zisku a ztráty",
	ARTICLES_OF_ASSOCIATION: "Zakladatelská listina",
	AUDITOR_REPORT: "Zpráva auditora",
	OTHER: "Ostatní",
};

function getDocumentTypeLabel(type: DocumentType): string {
	return DOCUMENT_TYPE_LABELS[type] ?? type;
}

function formatFileSize(bytes: number | null): string {
	if (bytes == null) return "";
	if (bytes >= 1_048_576) {
		return `${(bytes / 1_048_576).toFixed(1)} MB`;
	}
	if (bytes >= 1_024) {
		return `${(bytes / 1_024).toFixed(0)} kB`;
	}
	return `${bytes} B`;
}

function getStatusInfo(doc: CompanyDocument): {
	label: string;
	variant: "available" | "processing" | "unavailable";
} {
	if (doc.sourceUrl) {
		return { label: "Ke stažení", variant: "available" };
	}
	if (
		doc.ocrStatus === "PROCESSING" ||
		doc.ocrStatus === "QUEUED" ||
		doc.extractionStatus === "PROCESSING" ||
		doc.extractionStatus === "QUEUED"
	) {
		return { label: "Zpracovává se", variant: "processing" };
	}
	return { label: "Nedostupný", variant: "unavailable" };
}

const STATUS_CLASSES: Record<string, string> = {
	available: "bg-green-50 text-green-700 border border-green-200",
	processing: "bg-yellow-50 text-yellow-700 border border-yellow-200",
	unavailable: "bg-gray-50 text-gray-500 border border-gray-200",
};

// --- Component ---

interface DocumentsTabProps {
	ico: string;
	documents: CompanyDocument[];
}

export function DocumentsTab({ ico, documents }: DocumentsTabProps) {
	if (documents.length === 0) {
		return (
			<div className="py-8 text-center">
				<FileText className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
				<p className="text-sm font-medium text-muted-foreground">
					Žádné dokumenty nejsou k dispozici
				</p>
				<p className="text-xs text-muted-foreground mt-1">
					Dokumenty ze sbírky listin budou zobrazeny, jakmile budou zpracovány.
				</p>
			</div>
		);
	}

	// Group documents by year
	const grouped = groupByYear(documents);

	return (
		<div className="flex flex-col gap-6 pt-4">
			<div className="card-surface rounded-xl p-6">
				<div className="flex items-center gap-2 mb-4">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
						<FileText className="h-4 w-4 text-primary-600" />
					</div>
					<h2 className="text-lg font-semibold">Sbírka listin</h2>
					<span className="text-xs text-muted-foreground ml-auto">
						Celkem {documents.length}{" "}
						{documents.length === 1 ? "dokument" : documents.length < 5 ? "dokumenty" : "dokumentů"}
					</span>
				</div>

				<div className="flex flex-col gap-6">
					{grouped.map(({ year, docs }) => (
						<div key={year ?? "unknown"}>
							<h3 className="text-sm font-semibold text-muted-foreground mb-2 border-b border-gray-100 pb-1">
								{year ?? "Bez roku"}
							</h3>
							<div className="flex flex-col gap-2">
								{docs.map((doc) => (
									<DocumentRow key={doc.id} doc={doc} ico={ico} />
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function DocumentRow({ doc, ico }: { doc: CompanyDocument; ico: string }) {
	const status = getStatusInfo(doc);

	return (
		<div className="flex items-center gap-3 rounded-md border border-gray-100 px-3 py-2.5 hover:bg-gray-50 transition-colors">
			<FileText className="h-4 w-4 text-muted-foreground shrink-0" />

			<div className="flex flex-col gap-0.5 min-w-0 flex-1">
				<p className="text-sm font-medium truncate">{doc.title}</p>
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					<span>{getDocumentTypeLabel(doc.documentType)}</span>
					{doc.filingDate && (
						<>
							<span>·</span>
							<span>Podáno {formatDate(doc.filingDate)}</span>
						</>
					)}
					{doc.fileSize != null && (
						<>
							<span>·</span>
							<span>{formatFileSize(doc.fileSize)}</span>
						</>
					)}
					{doc.pageCount != null && (
						<>
							<span>·</span>
							<span>
								{doc.pageCount}{" "}
								{doc.pageCount === 1 ? "strana" : doc.pageCount < 5 ? "strany" : "stran"}
							</span>
						</>
					)}
				</div>
			</div>

			<span
				className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_CLASSES[status.variant]}`}
			>
				{status.label}
			</span>

			{doc.sourceUrl && (
				<a
					href={buildRegistryUrl(ico)}
					target="_blank"
					rel="noopener noreferrer"
					className="btn btn-xs btn-secondary-soft shrink-0 gap-1"
					title="Otevřít sbírku listin na verejnerejstriky.msp.gov.cz"
				>
					<ExternalLink className="h-3.5 w-3.5" />
					<span className="hidden sm:inline">Otevřít</span>
				</a>
			)}
		</div>
	);
}

function groupByYear(
	documents: CompanyDocument[],
): { year: number | null; docs: CompanyDocument[] }[] {
	const map = new Map<number | null, CompanyDocument[]>();

	for (const doc of documents) {
		const year = doc.year;
		const existing = map.get(year);
		if (existing) {
			existing.push(doc);
		} else {
			map.set(year, [doc]);
		}
	}

	// Sort groups: year descending, null last
	return Array.from(map.entries())
		.sort(([a], [b]) => {
			if (a == null && b == null) return 0;
			if (a == null) return 1;
			if (b == null) return -1;
			return b - a;
		})
		.map(([year, docs]) => ({ year, docs }));
}
